import { useEffect, useState } from 'react';
import { usePromptStore } from '../../../../store/usePrompt';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faCode, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

/**
 * @author taulmax
 * @date 2024.10.17
 * @description 프롬프트 생성 페이지 - 파라미터 리스트
 */
const PParamList = ({
  searchText,
  insertAtCursor,
}: {
  searchText: string;
  insertAtCursor: (text: string) => void;
}) => {
  const { promptParams, getPrompmtParams } = usePromptStore();
  const [hoveredParam, setHoveredParam] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);

  useEffect(() => {
    if (promptParams.length === 0) {
      getPrompmtParams();
    }
  }, [getPrompmtParams, promptParams.length]);

  const filteredParams = promptParams.filter(
    param =>
      param.desc.toLowerCase().includes(searchText.toLowerCase()) ||
      param.code.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleParamClick = (code: string) => {
    insertAtCursor('${' + code + '}');
    setShowTooltip(code);
    setTimeout(() => setShowTooltip(null), 1500);
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {filteredParams.length === 0 ? (
        <div className="text-center py-8 px-4">
          <FontAwesomeIcon icon={faTag} className="text-gray-300 text-2xl mb-2" />
          <p className="text-sm text-gray-500">
            {searchText ? '검색 결과가 없습니다' : '파라미터가 없습니다'}
          </p>
        </div>
      ) : (
        <div className="space-y-2 px-4">
          {filteredParams.map(param => (
            <div
              key={param.code}
              onClick={() => handleParamClick(param.code)}
              onMouseEnter={() => setHoveredParam(param.code)}
              onMouseLeave={() => setHoveredParam(null)}
              className="relative group cursor-pointer p-3 rounded-md bg-white border border-gray-200 hover:border-blue-400 hover:shadow-sm transition-all duration-200"
            >
              <div className="flex items-start gap-2">
                <FontAwesomeIcon 
                  icon={faTag} 
                  className="text-gray-400 mt-0.5 flex-shrink-0 text-sm" 
                />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors mb-1">
                    {param.desc}
                  </h4>
                  <code className="text-xs text-gray-600 font-mono bg-gray-100 px-2 py-0.5 rounded inline-block">
                    {'${' + param.code + '}'}
                  </code>
                </div>
              </div>

              {/* 예시 툴팁 */}
              {param.example && hoveredParam === param.code && (
                <div className="absolute left-full ml-2 top-0 z-10 w-64 p-2.5 bg-gray-800 text-white text-xs rounded-md shadow-md">
                  <div className="flex items-start gap-2">
                    <FontAwesomeIcon icon={faInfoCircle} className="flex-shrink-0 mt-0.5 text-gray-400" />
                    <div>
                      <p className="font-medium mb-0.5">예시:</p>
                      <p className="font-mono text-gray-300">{param.example}</p>
                    </div>
                  </div>
                  <div className="absolute left-0 top-3 -ml-2 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-gray-800" />
                </div>
              )}

              {/* 클릭 피드백 */}
              {showTooltip === param.code && (
                <div className="absolute right-2 top-2 px-2 py-1 bg-green-500 text-white text-xs rounded-md flex items-center gap-1 shadow-sm">
                  <FontAwesomeIcon icon={faCode} className="text-[10px]" />
                  <span className="font-medium">삽입됨</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* 사용 안내 */}
      {filteredParams.length > 0 && (
        <div className="mt-4 mx-4 p-2.5 bg-gray-50 rounded-md">
          <p className="text-xs text-gray-600 flex items-center gap-1.5">
            <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400" />
            <span>클릭하여 프롬프트에 파라미터를 삽입하세요</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default PParamList;